import React, { useState } from 'react';
import { Upload } from 'lucide-react';

interface ImageDropZoneProps {
  onImageDrop: (file: File) => void;
  isUploading: boolean;
  label?: string;
  showAlways?: boolean;
  compact?: boolean;
}

export const ImageDropZone: React.FC<ImageDropZoneProps> = ({
  onImageDrop,
  isUploading,
  label = 'Drop image here',
  showAlways = false,
  compact = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        onImageDrop(file);
      } else {
        alert('Please drop an image file');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        onImageDrop(file);
      } else {
        alert('Please select an image file');
      }
    }
  };

  const handleClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => handleFileSelect(e as any);
    input.click();
  };

  if (!showAlways && !isDragging && !isUploading) {
    return (
      <div
        onDragOver={handleDragOver}
        className={`absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-200 ${
          compact ? 'rounded-full' : ''
        }`}
      >
        <div
          className={`w-full h-full bg-black bg-opacity-70 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer ${
            compact ? 'rounded-full' : ''
          }`}
          onClick={handleClick}
        >
          <div className="text-center text-white">
            <Upload className={`${compact ? 'w-6 h-6' : 'w-12 h-12'} mx-auto mb-2`} />
            <p className={`${compact ? 'text-xs' : 'text-sm'} font-semibold`}>{label}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={!isUploading ? handleClick : undefined}
      className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-all ${
        showAlways
          ? `border-2 border-dashed ${
              isDragging
                ? 'border-yellow-400 bg-yellow-400 bg-opacity-20'
                : 'border-gray-300 bg-gray-800 bg-opacity-60'
            }`
          : `bg-black bg-opacity-70 ${isDragging ? 'bg-opacity-80 ring-2 ring-yellow-400' : ''}`
      } ${compact ? 'rounded-full' : ''}`}
    >
      <div className="text-center text-white">
        {isUploading ? (
          <>
            <div
              className={`animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-2`}
            />
            <p className="text-sm font-semibold">Uploading...</p>
          </>
        ) : (
          <>
            <Upload
              className={`${compact ? 'w-6 h-6' : 'w-12 h-12'} mx-auto mb-2 ${
                isDragging ? 'animate-bounce' : ''
              }`}
            />
            <p className={`${compact ? 'text-xs' : 'text-sm'} font-semibold`}>
              {isDragging ? 'Drop now!' : label}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
